import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PrintIcon from '@mui/icons-material/Print'
import DescriptionIcon from '@mui/icons-material/Description'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useChangeEnquiryStatus,
  useCompleteFollowup,
  useEnquiry,
  useLostReasons,
  useScheduleFollowup,
} from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatDateTime,
  formatMobile,
  formatMoney,
  formatNumber,
  humanise,
  InlineSpinner,
  PageHeader,
  StatusChip,
} from '@/components/common'

/**
 * The line the office walks. Lost and cancelled are not on it - they are how
 * an enquiry ends when it does not end in an order, so they are shown beside
 * the steps rather than as one.
 */
const FUNNEL = ['NEW', 'CONTACTED', 'QUOTATION_SENT', 'CONFIRMED']

export function EnquiryDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canWrite = useAuthStore((state) => state.canWrite)
  const can = useAuthStore((state) => state.can)
  const { data: enquiry, isLoading, error } = useEnquiry(id)

  const [updateDialog, setUpdateDialog] = useState(false)

  if (isLoading) return <InlineSpinner />
  if (error) return <ErrorBanner error={error} />
  if (!enquiry) return <EmptyState message="Enquiry not found." />

  const activeStep = FUNNEL.indexOf(enquiry.status)

  return (
    <>
      <PageHeader
        title={enquiry.enquiryNumber}
        subtitle={`${enquiry.customerName} · ${humanise(enquiry.eventType)} on ${formatDate(enquiry.eventDate)}`}
        actions={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/enquiries')}>
              Back
            </Button>
            {/* What we sell for this line of business, priced, in English or
                Tamil - the sheet the customer takes away from the counter.
                Offered to whoever may read the catalogue, since that is what
                it prints. */}
            {can('package.read') && (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => navigate(`/enquiries/${enquiry.id}/slip`)}
              >
                Menu sheet
              </Button>
            )}
            {canWrite('enquiry') && (
              <>
                <Button variant="outlined" onClick={() => setUpdateDialog(true)}>
                  Follow-up and status
                </Button>
              </>
            )}
            {canWrite('quotation') && !enquiry.convertedOrderId && (
              <Button
                variant="contained"
                startIcon={<DescriptionIcon />}
                onClick={() => navigate(`/quotations/new?enquiryId=${enquiry.id}`)}
              >
                Create quotation
              </Button>
            )}
          </Stack>
        }
      />

      {activeStep >= 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stepper activeStep={activeStep} alternativeLabel>
              {FUNNEL.map((stage) => (
                <Step key={stage}>
                  <StepLabel>{humanise(stage)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Requirement
              </Typography>
              <Grid container spacing={2}>
                <Field label="Service" value={humanise(enquiry.serviceType)} />
                <Field label="Event type" value={humanise(enquiry.eventType)} />
                <Field label="Event date" value={formatDate(enquiry.eventDate)} />
                <Field label="Timing" value={`${enquiry.startTime ?? '—'} to ${enquiry.endTime ?? '—'}`} />
                <Field label="Expected guests" value={formatNumber(enquiry.expectedGuestCount)} />
                <Field label="Food preference" value={humanise(enquiry.foodPreference)} />
                <Field label="Budget" value={formatMoney(enquiry.budgetAmount)} />
                <Field label="Weighted value" value={formatMoney(enquiry.estimatedValue)} />
                <Field label="Delivery address" value={enquiry.deliveryAddress ?? '—'} span={12} />
                <Field label="Special requirements" value={enquiry.specialRequirements ?? '—'} span={12} />
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Follow-ups
              </Typography>
              {enquiry.followups.length === 0 ? (
                <EmptyState message="No follow-ups recorded yet." />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Scheduled</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enquiry.followups.map((followup) => (
                      <FollowupRow key={followup.id} followup={followup} canWrite={canWrite('enquiry')} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Customer
              </Typography>
              <Typography variant="body2">{enquiry.customerName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatMobile(enquiry.customerMobile)}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <StatusChip status={enquiry.status} size="medium" />
                <Chip label={`${enquiry.probability}% likely`} variant="outlined" />
              </Stack>
              {enquiry.convertedOrderId && (
                <Button
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => navigate(`/orders/${enquiry.convertedOrderId}`)}
                >
                  Open the confirmed order
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Status history
              </Typography>
              {enquiry.statusHistory.length === 0 ? (
                <EmptyState message="No history yet." />
              ) : (
                <Stack spacing={1.5}>
                  {[...enquiry.statusHistory].reverse().map((entry, index) => (
                    <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                      <StatusChip status={entry.toStatus} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDateTime(entry.changedAt)}
                        </Typography>
                        {entry.remarks && <Typography variant="body2">{entry.remarks}</Typography>}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <EnquiryUpdateDialog
        open={updateDialog}
        enquiryId={enquiry.id}
        currentStatus={enquiry.status}
        onClose={() => setUpdateDialog(false)}
      />
    </>
  )
}

function Field({ label, value, span = 6 }: { label: string; value: React.ReactNode; span?: number }) {
  return (
    <Grid item xs={12} sm={span}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  )
}

function FollowupRow({
  followup,
  canWrite,
}: {
  followup: { id: string; scheduledAt?: string; followupType: string; status: string; outcome?: string; notes?: string }
  canWrite: boolean
}) {
  const complete = useCompleteFollowup()
  const [open, setOpen] = useState(false)
  const [outcome, setOutcome] = useState('CONNECTED')
  const [notes, setNotes] = useState('')
  const [nextAt, setNextAt] = useState('')

  return (
    <>
      <TableRow hover>
        <TableCell>{formatDateTime(followup.scheduledAt)}</TableCell>
        <TableCell>{humanise(followup.followupType)}</TableCell>
        <TableCell>
          <StatusChip status={followup.status} />
        </TableCell>
        <TableCell>{humanise(followup.outcome)}</TableCell>
        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {followup.notes ?? '—'}
        </TableCell>
        <TableCell align="right">
          {canWrite && followup.status === 'PENDING' && (
            <Button size="small" onClick={() => setOpen(true)}>
              Complete
            </Button>
          )}
        </TableCell>
      </TableRow>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete follow-up</DialogTitle>
        <DialogContent dividers>
          <ErrorBanner error={complete.error} />
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Outcome"
              value={outcome}
              onChange={(event) => setOutcome(event.target.value)}
            >
              {['CONNECTED', 'NO_ANSWER', 'RESCHEDULED', 'INTERESTED', 'NOT_INTERESTED'].map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Next follow-up"
              type="datetime-local"
              value={nextAt}
              onChange={(event) => setNextAt(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={complete.isPending}
            onClick={async () => {
              await complete.mutateAsync({
                followupId: followup.id,
                body: {
                  outcome,
                  notes: notes || undefined,
                  nextFollowupAt: nextAt ? new Date(nextAt).toISOString() : undefined,
                },
              })
              setOpen(false)
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/**
 * The status change and the next follow-up, taken in one place.
 *
 * <p>Either or both can be recorded. A status change is only sent when the
 * status is actually changed, and a follow-up only when a time is given.</p>
 */
function EnquiryUpdateDialog({
  open,
  enquiryId,
  currentStatus,
  onClose,
}: {
  open: boolean
  enquiryId: string
  currentStatus: string
  onClose: () => void
}) {
  const change = useChangeEnquiryStatus()
  const schedule = useScheduleFollowup()
  const { data: lostReasons } = useLostReasons()
  const [status, setStatus] = useState(currentStatus)
  const [remarks, setRemarks] = useState('')
  const [lostReasonId, setLostReasonId] = useState('')
  const [followupType, setFollowupType] = useState('CALL')
  const [scheduledAt, setScheduledAt] = useState('')
  const [notes, setNotes] = useState('')

  const statusChanged = status !== currentStatus
  const lostNeedsReason = status === 'LOST' && !lostReasonId
  const busy = change.isPending || schedule.isPending
  const canSave = !busy && (statusChanged || Boolean(scheduledAt)) && !lostNeedsReason

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Follow-up and status</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={change.error ?? schedule.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Status</Typography>
          <TextField
            select
            label="New status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            {[...FUNNEL, 'LOST', 'CANCELLED'].map((value) => (
              <MenuItem key={value} value={value}>
                {humanise(value)}
              </MenuItem>
            ))}
          </TextField>

          {status === 'LOST' && (
            <TextField
              select
              label="Lost reason"
              value={lostReasonId}
              onChange={(event) => setLostReasonId(event.target.value)}
              required
              helperText="A reason is required before an enquiry can be marked lost"
            >
              {lostReasons?.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label="Status remarks"
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            multiline
            minRows={2}
          />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Next follow-up</Typography>
          <TextField
            select
            label="Type"
            value={followupType}
            onChange={(event) => setFollowupType(event.target.value)}
          >
            {['CALL', 'EMAIL', 'WHATSAPP', 'VISIT', 'SITE_VISIT'].map((value) => (
              <MenuItem key={value} value={value}>
                {humanise(value)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Scheduled at"
            type="datetime-local"
            value={scheduledAt}
            onChange={(event) => setScheduledAt(event.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave blank if no follow-up is needed"
          />
          <TextField
            label="Follow-up notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={async () => {
            if (statusChanged) {
              await change.mutateAsync({
                id: enquiryId,
                body: {
                  status,
                  remarks: remarks || undefined,
                  lostReasonId: lostReasonId || undefined,
                },
              })
            }
            if (scheduledAt) {
              await schedule.mutateAsync({
                id: enquiryId,
                body: {
                  followupType,
                  scheduledAt: new Date(scheduledAt).toISOString(),
                  notes: notes || undefined,
                },
              })
            }
            onClose()
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
